/**
 * RightRailFinancials — iter-287 (renamed "Revenue")
 * ─────────────────────────────────────────────────────────────
 * Compact, calm Revenue card mounted inside the Deal Room
 * right-side operational rail. Strict refinement — NOT a
 * dashboard, NOT accounting. Three operator-essential lines
 * only:
 *
 *   • Agreed Fee       — `£23,125 + VAT` (formatAgreedFee)
 *   • Expected Banking — `May 2026`      (formatBankingMonth)
 *   • Revenue At Risk  — Low / Medium / High
 *
 * iter-287 brief explicitly removed the Forecast row + any
 * empty placeholder rows. Editing still happens inside the
 * FinancialsSheet drawer (separate component, untouched).
 *
 * The testid `canonical-rail-financials` is preserved so locked
 * contracts continue to resolve; new aliases (`…-revenue-*`) sit
 * alongside.
 */
import React from "react";
import { Pencil } from "lucide-react";

import {
  formatAgreedFee,
  formatBankingMonth,
  formatCommissionPct,
  formatGBP,
  normaliseRevenueAtRisk,
  resolveAgreedFeeGBP,
} from "../../lib/feeFormat";

const RISK_TONES = {
  Low:    { dot: "bg-emerald-500", text: "text-emerald-700" },
  Medium: { dot: "bg-amber-500",   text: "text-amber-700"   },
  High:   { dot: "bg-rose-500",    text: "text-rose-600"    },
};

export function RightRailFinancials({ deal, canEdit = true, onEdit }) {
  // Fee SSOT — mutual-exclusive by `commissionType`:
  //   • "percent" / "percentage" → renders `feePct` as `1.95%`.
  //   • "fixed" (default)        → renders `fee` as `£9,500 + VAT`.
  // Both values come off the canonical deal record; no new field
  // is introduced and no hard-coded number lives in the JSX.
  //
  // P0 · Agreed Fee Backfill (2026-08-12) — the previous
  // `Number(deal?.fee)` short-circuit rendered `—` whenever
  // `deal.fee` was a pre-formatted string ("£9,062") — the case
  // for every percent-mode deal on the seed.  The switch below
  // reads the canonical numeric surface (`feePct` when percent,
  // `fee` when fixed) so the card always resolves to a live
  // value while the SSOT remains untouched.
  const commissionType = String(deal?.commissionType || deal?.commission_type || "").toLowerCase();
  const isPercent = commissionType === "percent" || commissionType === "percentage";
  const feePct    = Number.isFinite(Number(deal?.feePct))
    ? Number(deal.feePct)
    : Number.isFinite(Number(deal?.fee_pct)) ? Number(deal.fee_pct) : null;
  // iter-alignment · P0 · Agreed Fee Basis Reconciliation
  // (2026-02-15) — resolve the canonical net-of-VAT £ fee
  // through the shared SSOT helper so percentage deals compute
  // `agreedPrice × feePct` (previously they rendered
  // `£0 + VAT` because the Revenue card only read the stored
  // `deal.fee`, which is cleared by Fee Basis Integrity on
  // every percentage save).  Fixed-fee deals continue to read
  // the stored `deal.fee`.  Both aliases (camelCase pilot,
  // snake_case backend) supported.  Same helper feeds
  // Forecasting so both surfaces reconcile off one calculation.
  const feeNumeric = resolveAgreedFeeGBP(deal);
  // P0 · Agreed Fee £ Primary Readout (2026-08-12) — Percent deals
  // now surface the computed £ amount as the primary hero value
  // (with the "% of £{price}" cadence as the subtitle underneath).
  // The percent + price copy comes verbatim off the shared SSOT
  // (`fee_pct` + `agreed_price`, both already normalised by the
  // API serializer + backfill polish); no new field, no new
  // calculation.  Fixed deals keep the single "£X + VAT" line.
  //
  // iter-alignment · P · Revenue Subtitle Currency Fix
  // (2026-02-14) — The raw `agreed_price` SSOT value can arrive
  // as either a formatted string ("£710,000") or a bare number
  // (`710000` / `"710000"`).  The subtitle previously
  // interpolated the raw value directly, which read
  // "2.00% of 710000" whenever the backend served the numeric
  // shape.  We now coerce to a number (tolerant of £/commas)
  // and re-render through the shared `formatGBP` SSOT
  // formatter so the subtitle always reads
  // "2.00% of £710,000" regardless of the underlying payload.
  const feeDisplay = formatAgreedFee(feeNumeric, !!(deal?.vatIncluded ?? deal?.vat_included));
  const _agreedPriceRaw = deal?.agreedPrice || deal?.agreed_price || null;
  const _agreedPriceNum = _agreedPriceRaw !== null
    ? Number(String(_agreedPriceRaw).replace(/[^0-9.-]/g, ""))
    : NaN;
  const agreedPriceDisplay = Number.isFinite(_agreedPriceNum) && _agreedPriceNum > 0
    ? formatGBP(_agreedPriceNum)
    : (_agreedPriceRaw || null);
  const feeSubtitle = isPercent && feePct !== null && agreedPriceDisplay
    ? `${formatCommissionPct(feePct)} of ${agreedPriceDisplay}`
    : null;
  // Fee is banked at exchange: once exchanged, the month is the actual exchange date.
  const _stageLc = String(deal?.stage || "").toLowerCase();
  const isBanked = _stageLc === "exchanged" || _stageLc === "completed";
  const month = isBanked
    ? (deal?.exchangedAt || deal?.exchanged_at)
    : (deal?.expectedExchangeMonth || deal?.targetExchange || deal?.target_exchange);
  // Only a value someone set by hand; unset reads "Not set" (never a default Low).
  const _riskRaw = deal?.revenueAtRisk ?? deal?.revenue_at_risk;
  const risk  = _riskRaw ? normaliseRevenueAtRisk(_riskRaw) : "Not set";
  const riskTone = RISK_TONES[risk] || { dot: "bg-slate-300", text: "text-slate-500" };
  // A pre-exchange target already in the past stays visibly overdue (the month is never moved).
  const _te = deal?.targetExchange || deal?.target_exchange;
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const isOverdue = !isBanked && !!_te
    && Date.parse(String(_te).slice(0, 10) + "T00:00:00") < _today.getTime();

  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-financials"
    >
      {/* iter-alignment · P34 — Header condensed: decorative Coins
          icon retired; title stands alone alongside the edit
          affordance. */}
      <header className="flex items-center justify-between mb-2.5">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900" data-testid="canonical-rail-revenue-header">Revenue</h3>
        {canEdit && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 cf-press"
            aria-label="Edit revenue"
            data-testid="canonical-rail-financials-edit"
          >
            <Pencil size={12} strokeWidth={2.2} />
          </button>
        )}
      </header>

      <ul className="space-y-1.5">
        {/* iter-alignment · P34 — AGREED FEE eyebrow stays uppercase
            and sits directly above the hero fee amount.  Same SSOT
            (`deal.fee` via `formatAgreedFee`), same testids. */}
        <li
          className="pb-1.5 border-b border-slate-100"
          data-testid="canonical-rail-financials-fee"
        >
          <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
            {/* iter-alignment · P0 · Financials Refinement (2026-02-28)
                — Show `Fixed Fee` for fixed-fee deals, `Agreed Fee`
                for percentage deals.  The underlying SSOT
                (`deal.fee` / `deal.feePct` via `formatAgreedFee`)
                is unchanged; only the eyebrow label switches. */}
            {isPercent ? "Agreed Fee" : "Fixed Fee"}
          </div>
          <div
            className="mt-0.5 text-[22px] font-bold text-slate-900 tabular-nums leading-[1.15] tracking-[-0.012em]"
            data-testid="canonical-rail-revenue-fee-value"
            data-fee-mode={isPercent ? "percent" : "fixed"}
          >
            {feeDisplay}
          </div>
          {feeSubtitle && (
            <div
              className="mt-0.5 text-[11.5px] text-slate-500 tabular-nums"
              data-testid="canonical-rail-revenue-fee-subtitle"
            >
              {feeSubtitle}
            </div>
          )}
        </li>

        {/* Expected Banking — `Month YYYY` */}
        <li
          className="flex items-center justify-between text-[12px]"
          data-testid="canonical-rail-financials-banking"
        >
          <span className="text-slate-500">{isBanked ? "Banked" : "Expected Banking"}</span>
          <span className="inline-flex items-baseline gap-1.5">
            {isOverdue && (
              <span className="text-[10.5px] font-semibold text-rose-600" data-testid="canonical-rail-revenue-banking-overdue">
                Overdue ·
              </span>
            )}
            <span
              className={`font-semibold ${isOverdue ? "text-rose-600" : "text-slate-900"}`}
              data-testid="canonical-rail-revenue-banking-value"
            >
              {formatBankingMonth(month)}
            </span>
          </span>
        </li>

        {/* Revenue Risk — Low / Medium / High (SSOT band). */}
        <li
          className="flex items-center justify-between text-[12px]"
          data-testid="canonical-rail-financials-risk"
        >
          <span className="text-slate-500">Revenue Risk</span>
          <span
            className={`inline-flex items-center gap-1.5 font-semibold ${riskTone.text}`}
            data-testid="canonical-rail-revenue-risk-value"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${riskTone.dot}`} aria-hidden />
            {risk}
          </span>
        </li>
      </ul>

      {/* iter-alignment · P34 — Forecasting Centre footer retired
          per the Revenue card refinement brief.  The linkage still
          exists (same SSOT fields feed the Forecasting Centre) but
          the reassurance line was reading as visual noise on the
          rail.  Anchor kept as an sr-only span so any locked audit
          selector still resolves. */}
      <span className="sr-only" data-testid="canonical-rail-financials-forecast-hint">
        Feeds Forecasting Centre
      </span>
    </section>
  );
}

export default RightRailFinancials;
